import { Icons } from '@/components/icons';
import type {
  Filter,
  FilterEnumChoice,
} from '@/components/table-filters/constants';
import {
  BOOLEAN_FILTER,
  defaultBooleanFilter,
  defaultDateFilter,
  defaultEnumFilter,
  defaultNumberFilter,
  defaultRelationFilter,
  defaultStringFilter,
  OPERATORS,
} from '@/components/table-filters/constants';
import {
  CompanyConfig,
  InvoiceConfig,
  RealEstateConfig,
  TenantConfig,
} from '@pigello/pigello-matrix';
import baseFilters, {
  baseGlobalSegmentsFilter,
  baseTagFilter,
} from '../../filters';

const INVOICE_STATUS_FILTERS = (
  kind: 'rental' | 'non_rental' | 'undetermined' = 'undetermined'
): FilterEnumChoice[] => [
  {
    filters: [{ operator: 'noop', value: 'credit_rental_invoices' }],
    title: 'Kreditavier',
    id: 'credit_rental_invoices',
    badgeVariant: 'outline',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [{ operator: 'noop', value: 'credit_invoices' }],
    title: 'Kreditfakturor',
    id: 'credit_invoices',
    badgeVariant: 'outline',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [{ operator: 'noop', value: 'not_attested' }],
    title: `Ej attesterade ${kind === 'rental' ? 'avier' : kind === 'non_rental' ? 'fakturor' : ''}`,
    badgeVariant: 'yellow',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [{ operator: 'noop', value: 'failed_attested' }],
    title: 'Misslyckade attesteringar',
    badgeVariant: 'destructive',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'open',
      },
    ],
    title: 'Öppna',
    badgeVariant: 'blue',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'fully_paid_with_open_debt',
      },
    ],
    title: 'Slutbetalda (Öppna påminnelser/krav)',
    badgeVariant: 'orange',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'fully_paid',
      },
    ],
    title: 'Slutbetalda',
    badgeVariant: 'green',
    overrideExternalFieldName: 'custom_invoice_filter',
  },

  {
    filters: [
      {
        operator: 'noop',
        value: 'overdue',
      },
    ],
    title: 'Förfallna',
    badgeVariant: 'destructive',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'reminderspending',
      },
    ],
    title: 'Automatiska påminnelser aktivt',
    badgeVariant: 'purple',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'reminders',
      },
    ],
    title: 'Påminnelse skickad',
    badgeVariant: 'purple',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
  {
    filters: [
      {
        operator: 'noop',
        value: 'debt',
      },
    ],
    title: 'Kravhantering aktiv',
    badgeVariant: 'purple',
    overrideExternalFieldName: 'custom_invoice_filter',
  },
];

const invoiceFilters = (
  kind: 'rental' | 'non_rental' | 'undetermined' = 'undetermined'
): Filter[] => [
  {
    ...defaultEnumFilter,
    title: 'Status',
    externalFieldName: 'custom_invoice_filter',
    enumChoices: INVOICE_STATUS_FILTERS(kind).filter((f) => {
      if (kind === 'rental' && f.id === 'credit_invoices') return false;
      if (kind === 'non_rental' && f.id === 'credit_rental_invoices')
        return false;
      return true;
    }),
  },
  {
    ...defaultNumberFilter,
    title: InvoiceConfig.fields.invoiceNumber.verboseName,
    externalFieldName: InvoiceConfig.fields.invoiceNumber.externalFieldName,
    icon: Icons.number,
  },
  {
    ...defaultStringFilter,
    title: InvoiceConfig.fields.ocrNumber.verboseName,
    externalFieldName: InvoiceConfig.fields.ocrNumber.externalFieldName,
    icon: Icons.number,
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.attestedDate.verboseName,
    externalFieldName: InvoiceConfig.fields.attestedDate.externalFieldName,
    defaultOperator: OPERATORS.DATE_PAST[0],
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.closedTime.verboseName,
    externalFieldName: InvoiceConfig.fields.closedTime.externalFieldName,
    defaultOperator: OPERATORS.DATE_PAST[0],
  },
  {
    ...defaultDateFilter,
    title:
      kind === 'rental'
        ? 'Avidatum'
        : InvoiceConfig.fields.invoiceDate.verboseName,
    externalFieldName: InvoiceConfig.fields.invoiceDate.externalFieldName,
    defaultOperator: OPERATORS.DATE_PAST[0],
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.dueDate.verboseName,
    externalFieldName: InvoiceConfig.fields.dueDate.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.mainPeriodStart.verboseName,
    externalFieldName: InvoiceConfig.fields.mainPeriodStart.externalFieldName,
  },
  {
    ...defaultDateFilter,
    title: InvoiceConfig.fields.mainPeriodEnd.verboseName,
    externalFieldName: InvoiceConfig.fields.mainPeriodEnd.externalFieldName,
  },
  {
    ...defaultRelationFilter,
    title: InvoiceConfig.fields.company.verboseName,
    externalFieldName: InvoiceConfig.fields.company.externalFieldName,
    relationModelName: CompanyConfig.modelName,
    icon: Icons.company,
  },
  {
    ...defaultRelationFilter,
    title: 'Fastighet',
    externalFieldName: InvoiceConfig.fields.realestate.externalFieldName,
    relationModelName: RealEstateConfig.modelName,
    icon: Icons.realestate,
  },
  ...baseGlobalSegmentsFilter,

  {
    ...defaultBooleanFilter,
    title: InvoiceConfig.fields.useRounding.verboseName,
    externalFieldName: InvoiceConfig.fields.useRounding.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: InvoiceConfig.fields.utilizeCustomerBalance.verboseName,
    externalFieldName:
      InvoiceConfig.fields.utilizeCustomerBalance.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: InvoiceConfig.fields.autogiroEnabled.verboseName,
    externalFieldName: InvoiceConfig.fields.autogiroEnabled.externalFieldName,
  },
  {
    ...defaultBooleanFilter,
    title: InvoiceConfig.fields.internalInvoice.verboseName,
    externalFieldName: InvoiceConfig.fields.internalInvoice.externalFieldName,
  },
  {
    kind: BOOLEAN_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.boolean,
    title: 'Har misslyckats med autogirodragning',
    externalFieldName: 'has_failed_autogiro_payment',
  },
  {
    kind: BOOLEAN_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.boolean,
    title: 'Har väntande autogirodragning',
    externalFieldName: 'has_delivered_autogiro_payment',
  },
  {
    kind: BOOLEAN_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.boolean,
    title: 'Har lyckad autogirodragning',
    externalFieldName: 'has_successful_autogiro_payment',
  },
  {
    kind: BOOLEAN_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.boolean,
    title: 'Automatiska påminnelser pausade',
    externalFieldName: 'automatic_debt_invoicing_paused',
  },
  {
    ...defaultNumberFilter,
    title: 'Originalbelopp brutto (SEK)',
    externalFieldName: 'total_value',
    icon: Icons.fee,
  },
  {
    ...defaultNumberFilter,
    title: 'Betalt belopp (SEK)',
    externalFieldName: 'payments_sum',
    icon: Icons.fee,
  },
  {
    ...defaultNumberFilter,
    title: 'Kvarvarande belopp (SEK)',
    externalFieldName: 'remaining_amount',
    defaultOperator: OPERATORS.LTE[0],
    icon: Icons.fee,
  },
  {
    ...defaultNumberFilter,
    title: 'Antal öppna påminnelser/krav',
    externalFieldName: 'unclosed_debt_invoices',
    icon: Icons.number,
  },
  {
    ...defaultNumberFilter,
    title: 'Antal påminnelser',
    externalFieldName: 'debt_invoice_counts_kind_0',
    icon: Icons.number,
  },
  {
    ...defaultNumberFilter,
    title: 'Antal krav',
    externalFieldName: 'debt_invoice_counts_kind_1',
    icon: Icons.number,
  },
  {
    ...defaultNumberFilter,
    title: 'Antal kravpåminnelser',
    externalFieldName: 'debt_invoice_counts_debt_stage_0',
    icon: Icons.number,
  },
  {
    ...defaultNumberFilter,
    title: 'Antal inkasso',
    externalFieldName: 'debt_invoice_counts_debt_stage_1',
    icon: Icons.number,
  },
  {
    ...defaultNumberFilter,
    title: 'Antal kronofogden',
    externalFieldName: 'debt_invoice_counts_debt_stage_2',
    icon: Icons.number,
  },
  {
    ...defaultRelationFilter,
    title: 'Hyresgäst',
    relationModelName: TenantConfig.modelName,
    externalFieldName: 'tenant',
    operators: ['noop'],
    defaultOperator: 'noop',
    icon: Icons.tenant,
  },

  ...baseTagFilter,
  ...baseFilters,
];

export default invoiceFilters;
